import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  getCatalogoDiagnosticosControllerListarQueryKey,
  getCatalogoDiagnosticosControllerObterQueryKey,
  useCatalogoDiagnosticosControllerFundir,
  useCatalogoDiagnosticosControllerListar,
} from '@/api/generated/admin-catalogo-diagnosticos/admin-catalogo-diagnosticos';
import type { CatalogoDiagnosticoResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { Campo } from '@/shared/components/Campo';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { ComboBox } from '@/shared/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { toast } from '@/shared/ui/sonner';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** O diagnóstico que SOME da escolha — o duplicado. */
  diagnostico: CatalogoDiagnosticoResponse;
}

/**
 * Fundir uma duplicata no diagnóstico que fica.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ Por que a fusão existe, e por que não basta desativar:
 *
 *  "Cushing" e "Hiperadrenocorticismo" cadastrados como duas linhas são a
 *  MESMA doença com duas coortes. Sem a fusão, a única saída é desativar uma
 *  das duas — e aí todo paciente registrado pela linha desativada some da
 *  Análise Comparativa, EM SILÊNCIO: os casos continuam no banco, a estatística
 *  muda, e ninguém recebe um aviso.
 *
 *  A fusão aponta `substituidoPorId` e desativa. Os diagnósticos de paciente
 *  antigos continuam apontando para o item antigo de propósito — é a LEITURA
 *  que segue a seta. Repontá-los reescreveria o que o clínico escolheu na
 *  época.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function FundirDiagnosticoDialogo({ aberto, aoFechar, diagnostico }: Props) {
  const queryClient = useQueryClient();

  const [destinoId, setDestinoId] = useState('');
  const [destinoNome, setDestinoNome] = useState('');
  const [termo, setTermo] = useState('');
  const [erroDoServidor, setErroDoServidor] = useState<string | null>(null);

  const termoAdiado = useDebounce(termo);

  const fundir = useCatalogoDiagnosticosControllerFundir();

  // Só ATIVOS: o servidor recusa destino desativado com 409, e oferecer na
  // lista o que ele vai recusar é convidar ao erro.
  const { data: candidatos, isFetching } = useCatalogoDiagnosticosControllerListar(
    {
      pagina: 1,
      porPagina: 20,
      ordenarPor: 'nome',
      ordem: 'asc',
      ativo: 'true',
      ...(termoAdiado.trim() ? { busca: termoAdiado.trim() } : {}),
    },
    { query: { enabled: aberto } },
  );

  useEffect(() => {
    if (aberto) return;
    setDestinoId('');
    setDestinoNome('');
    setTermo('');
    setErroDoServidor(null);
  }, [aberto]);

  // O próprio item fora da lista: o servidor recusa a auto-fusão com 400, e a
  // tela não deve nem deixar escolher.
  const opcoes = (candidatos?.itens ?? [])
    .filter((c) => c.id !== diagnostico.id)
    .map((c) => ({
      valor: c.id,
      rotulo: c.nome,
      ...(c.categoria ? { detalhe: c.categoria } : {}),
    }));

  async function confirmar() {
    if (!destinoId) return;
    setErroDoServidor(null);

    try {
      await fundir.mutateAsync({ id: diagnostico.id, data: { substituidoPorId: destinoId } });

      await queryClient.invalidateQueries({
        queryKey: getCatalogoDiagnosticosControllerListarQueryKey(),
      });
      await queryClient.invalidateQueries({
        queryKey: getCatalogoDiagnosticosControllerObterQueryKey(diagnostico.id),
      });

      toast.success(`"${diagnostico.nome}" foi fundido em "${destinoNome}".`);
      aoFechar();
    } catch (erro) {
      // A mensagem do servidor diz qual das três recusas foi (auto-fusão,
      // destino inativo, destino já fundido). Um "erro ao fundir" genérico
      // jogaria isso fora.
      setErroDoServidor(
        erro instanceof ApiError ? erro.message : 'Não foi possível fundir o diagnóstico.',
      );
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Fundir &quot;{diagnostico.nome}&quot; em outro diagnóstico</DialogTitle>
          <DialogDescription>
            Sem a fusão, a única saída para uma duplicata é desativar uma das duas — e aí a coorte
            de quem registrou pela linha desativada some da Análise Comparativa em silêncio.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {erroDoServidor && (
            <Alert variant="destructive">
              <AlertDescription>{erroDoServidor}</AlertDescription>
            </Alert>
          )}

          <Campo
            id="destino"
            rotulo="Diagnóstico que fica"
            obrigatorio
            dica={`"${diagnostico.nome}" será desativado e passará a apontar para o escolhido. Os ${diagnostico.totalEmUso} registro(s) de paciente não são reescritos: é a leitura que segue a seta.`}
          >
            <ComboBox
              id="destino"
              valor={destinoId}
              rotuloSelecionado={destinoNome}
              aoEscolher={(v) => {
                setDestinoId(v);
                setDestinoNome(opcoes.find((o) => o.valor === v)?.rotulo ?? '');
                setErroDoServidor(null);
              }}
              opcoes={opcoes}
              placeholder="Escolha o diagnóstico que fica"
              placeholderBusca="Buscar por nome ou sigla..."
              vazio="Nenhum outro diagnóstico ativo encontrado."
              aoBuscar={setTermo}
              carregando={isFetching}
            />
          </Campo>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={aoFechar} disabled={fundir.isPending}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => void confirmar()}
            disabled={!destinoId || fundir.isPending}
          >
            {fundir.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Fundir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
